import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../services/firebase';
import { collection, query, where, getDocs, orderBy } from 'firebase/firestore';
import Header from '../components/layout/Header';
import {
  FiArrowLeft,
  FiFileText,
  FiDownload,
  FiAlertCircle,
  FiInfo,
  FiCalendar,
  FiDollarSign,
  FiCheckCircle
} from 'react-icons/fi';

interface TaxForm1098T {
  id: string;
  taxYear: number;
  studentName: string;
  studentSSN: string;
  studentId: string;
  address: {
    street: string;
    city: string;
    state: string;
    zipCode: string;
  };
  box1_PaymentsReceived: number;
  box2_AmountsBilled: number;
  box4_Adjustments: number;
  box5_Scholarships: number;
  box6_AdjustmentsPriorYear: number;
  box7_Checked: boolean; // Box 7: Check if box 2 includes amounts for an academic period beginning January - March
  box8_HalfTime: boolean;
  box9_Graduate: boolean;
  box10_Insurance: number;
  issuedDate: string;
  available: boolean;
}

const My1098TPage: React.FC = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [taxForms, setTaxForms] = useState<TaxForm1098T[]>([]);
  const [selectedForm, setSelectedForm] = useState<TaxForm1098T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchTaxForms = async () => {
      if (!currentUser) return;

      try {
        setLoading(true);
        setError(null);

        const formsQuery = query(
          collection(db, 'taxForms1098T'),
          where('userId', '==', currentUser.uid),
          orderBy('taxYear', 'desc')
        );

        const formsSnapshot = await getDocs(formsQuery);
        const forms = formsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        } as TaxForm1098T));

        setTaxForms(forms);
        if (forms.length > 0) {
          setSelectedForm(forms[0]);
        }
      } catch (err) {
        console.error('Error fetching tax forms:', err);
        setError('Failed to load tax forms. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchTaxForms();
  }, [currentUser]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const handleDownload = (form: TaxForm1098T) => {
    // In a real implementation, this would generate and download a PDF
    alert(`Downloading 1098-T for tax year ${form.taxYear}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100">
        <Header />
        <main className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex items-center justify-center h-64">
            <div className="text-uc-navy">Loading tax forms...</div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <Header />

      <main className="max-w-7xl mx-auto px-4 py-6">
        {/* Navigation */}
        <div className="mb-6">
          <button
            onClick={() => navigate('/home')}
            className="flex items-center text-uc-navy hover:text-uc-gold transition-colors mb-4"
          >
            <FiArrowLeft size={20} className="mr-2" />
            Back to Home
          </button>
          <h1 className="text-3xl font-bold text-uc-navy font-header">MY 1098-T TAX FORM</h1>
          <p className="text-gray-600 mt-2">View and download your tuition tax statement</p>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 flex items-start gap-3">
            <FiAlertCircle className="text-red-600 mt-0.5" size={20} />
            <div>
              <p className="text-red-800 font-medium">Error</p>
              <p className="text-red-700 text-sm">{error}</p>
            </div>
          </div>
        )}

        {/* Information Banner */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-6">
          <div className="flex items-start gap-4">
            <FiInfo className="text-blue-600 mt-1" size={24} />
            <div className="flex-1">
              <h3 className="text-lg font-bold text-blue-900 mb-2">About Form 1098-T</h3>
              <p className="text-blue-800 mb-2">
                Form 1098-T, Tuition Statement, is a tax form filed by educational institutions showing the amount
                of qualified tuition and related expenses paid during the tax year. You may be able to claim education
                tax credits based on this information.
              </p>
              <p className="text-blue-800 text-sm">
                Forms are typically available by January 31st for the previous tax year.
              </p>
            </div>
          </div>
        </div>

        {/* Tax Year Selector */}
        {taxForms.length > 0 && (
          <div className="bg-white rounded-lg shadow p-6 mb-6">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-3">
                <FiCalendar className="text-uc-gold" size={24} />
                <h2 className="text-xl font-bold text-uc-navy">Select Tax Year</h2>
              </div>
              <div className="flex gap-3 flex-wrap">
                {taxForms.map((form) => (
                  <button
                    key={form.id}
                    onClick={() => setSelectedForm(form)}
                    className={`px-6 py-2 rounded-lg font-medium transition-colors ${
                      selectedForm?.id === form.id
                        ? 'bg-uc-gold text-white'
                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    }`}
                  >
                    {form.taxYear}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {taxForms.length === 0 ? (
          <div className="bg-white rounded-lg shadow p-12 text-center">
            <FiFileText size={64} className="mx-auto text-gray-300 mb-4" />
            <h2 className="text-2xl font-bold text-uc-navy mb-2">No Tax Forms Available</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              You don't have any 1098-T forms available at this time. Forms are typically available
              by January 31st for the previous tax year.
            </p>
          </div>
        ) : selectedForm && (
          <>
            {/* Form Header */}
            <div className="bg-white rounded-lg shadow mb-6">
              <div className="p-6 border-b border-gray-200 flex items-center justify-between flex-wrap gap-4">
                <div>
                  <h2 className="text-2xl font-bold text-uc-navy mb-1">
                    Form 1098-T for Tax Year {selectedForm.taxYear}
                  </h2>
                  <p className="text-gray-600 text-sm">
                    Issued: {formatDate(selectedForm.issuedDate)}
                  </p>
                </div>
                <button
                  onClick={() => handleDownload(selectedForm)}
                  className="flex items-center gap-2 bg-uc-gold text-white px-6 py-3 rounded-lg hover:bg-yellow-500 transition-colors font-medium"
                >
                  <FiDownload size={18} />
                  Download PDF
                </button>
              </div>

              {/* Student Information */}
              <div className="p-6 bg-gray-50">
                <h3 className="font-bold text-uc-navy mb-4">Student Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Name</p>
                    <p className="text-gray-900 font-medium">{selectedForm.studentName}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Student ID</p>
                    <p className="text-gray-900 font-medium">{selectedForm.studentId}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 mb-1">SSN (Last 4 digits)</p>
                    <p className="text-gray-900 font-medium">***-**-{selectedForm.studentSSN.slice(-4)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Address</p>
                    <p className="text-gray-900">
                      {selectedForm.address.street}<br />
                      {selectedForm.address.city}, {selectedForm.address.state} {selectedForm.address.zipCode}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Form Boxes */}
            <div className="bg-white rounded-lg shadow mb-6">
              <div className="p-6 border-b border-gray-200">
                <h3 className="text-xl font-bold text-uc-navy">Form Details</h3>
              </div>

              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Box 1 */}
                  <div className="border border-gray-300 rounded-lg p-4">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <p className="text-xs text-gray-600 font-medium">BOX 1</p>
                        <p className="text-sm text-gray-700 mt-1">Payments Received for Qualified Tuition</p>
                      </div>
                      <FiDollarSign className="text-uc-gold" size={20} />
                    </div>
                    <p className="text-2xl font-bold text-uc-navy">
                      {formatCurrency(selectedForm.box1_PaymentsReceived)}
                    </p>
                  </div>

                  {/* Box 2 */}
                  <div className="border border-gray-300 rounded-lg p-4">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <p className="text-xs text-gray-600 font-medium">BOX 2</p>
                        <p className="text-sm text-gray-700 mt-1">Amounts Billed for Qualified Tuition</p>
                      </div>
                      <FiDollarSign className="text-uc-gold" size={20} />
                    </div>
                    <p className="text-2xl font-bold text-uc-navy">
                      {formatCurrency(selectedForm.box2_AmountsBilled)}
                    </p>
                  </div>

                  {/* Box 4 */}
                  <div className="border border-gray-300 rounded-lg p-4">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <p className="text-xs text-gray-600 font-medium">BOX 4</p>
                        <p className="text-sm text-gray-700 mt-1">Adjustments Made for Prior Year</p>
                      </div>
                      <FiDollarSign className="text-uc-gold" size={20} />
                    </div>
                    <p className="text-2xl font-bold text-uc-navy">
                      {formatCurrency(selectedForm.box4_Adjustments)}
                    </p>
                  </div>

                  {/* Box 5 */}
                  <div className="border border-gray-300 rounded-lg p-4">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <p className="text-xs text-gray-600 font-medium">BOX 5</p>
                        <p className="text-sm text-gray-700 mt-1">Scholarships or Grants</p>
                      </div>
                      <FiDollarSign className="text-uc-gold" size={20} />
                    </div>
                    <p className="text-2xl font-bold text-uc-navy">
                      {formatCurrency(selectedForm.box5_Scholarships)}
                    </p>
                  </div>

                  {/* Box 6 */}
                  <div className="border border-gray-300 rounded-lg p-4">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <p className="text-xs text-gray-600 font-medium">BOX 6</p>
                        <p className="text-sm text-gray-700 mt-1">Adjustments to Scholarships for Prior Year</p>
                      </div>
                      <FiDollarSign className="text-uc-gold" size={20} />
                    </div>
                    <p className="text-2xl font-bold text-uc-navy">
                      {formatCurrency(selectedForm.box6_AdjustmentsPriorYear)}
                    </p>
                  </div>

                  {/* Box 10 */}
                  <div className="border border-gray-300 rounded-lg p-4">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <p className="text-xs text-gray-600 font-medium">BOX 10</p>
                        <p className="text-sm text-gray-700 mt-1">Amounts for Health Insurance</p>
                      </div>
                      <FiDollarSign className="text-uc-gold" size={20} />
                    </div>
                    <p className="text-2xl font-bold text-uc-navy">
                      {formatCurrency(selectedForm.box10_Insurance)}
                    </p>
                  </div>
                </div>

                {/* Checkboxes */}
                <div className="mt-6 pt-6 border-t border-gray-200">
                  <h4 className="font-bold text-uc-navy mb-4">Additional Information</h4>
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <div className={`w-6 h-6 rounded border-2 flex items-center justify-center ${
                        selectedForm.box7_Checked
                          ? 'bg-uc-gold border-uc-gold'
                          : 'border-gray-300'
                      }`}>
                        {selectedForm.box7_Checked && <FiCheckCircle className="text-white" size={16} />}
                      </div>
                      <p className="text-gray-700">
                        <span className="font-medium">Box 7:</span> Amount in Box 2 includes expenses for an academic
                        period beginning January-March of next year
                      </p>
                    </div>

                    <div className="flex items-center gap-3">
                      <div className={`w-6 h-6 rounded border-2 flex items-center justify-center ${
                        selectedForm.box8_HalfTime
                          ? 'bg-uc-gold border-uc-gold'
                          : 'border-gray-300'
                      }`}>
                        {selectedForm.box8_HalfTime && <FiCheckCircle className="text-white" size={16} />}
                      </div>
                      <p className="text-gray-700">
                        <span className="font-medium">Box 8:</span> Student was enrolled at least half-time
                      </p>
                    </div>

                    <div className="flex items-center gap-3">
                      <div className={`w-6 h-6 rounded border-2 flex items-center justify-center ${
                        selectedForm.box9_Graduate
                          ? 'bg-uc-gold border-uc-gold'
                          : 'border-gray-300'
                      }`}>
                        {selectedForm.box9_Graduate && <FiCheckCircle className="text-white" size={16} />}
                      </div>
                      <p className="text-gray-700">
                        <span className="font-medium">Box 9:</span> Student was enrolled in a graduate program
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Institution Information */}
            <div className="bg-white rounded-lg shadow mb-6">
              <div className="p-6 border-b border-gray-200">
                <h3 className="text-xl font-bold text-uc-navy">Institution Information</h3>
              </div>

              <div className="p-6">
                <div className="space-y-2">
                  <p className="text-gray-900 font-bold">University of California, Merced</p>
                  <p className="text-gray-700">5200 N Lake Rd</p>
                  <p className="text-gray-700">Merced, CA 95343</p>
                  <p className="text-gray-700 mt-3">
                    <span className="font-medium">Federal EIN:</span> 95-6006143
                  </p>
                  <p className="text-gray-700">
                    <span className="font-medium">Phone:</span> (209) 228-4400
                  </p>
                </div>
              </div>
            </div>

            {/* Important Tax Information */}
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
              <h3 className="font-bold text-yellow-900 mb-3 flex items-center gap-2">
                <FiAlertCircle size={20} />
                Important Tax Information
              </h3>
              <ul className="space-y-2 text-sm text-yellow-800">
                <li className="flex items-start gap-2">
                  <span className="text-yellow-600 mt-1">•</span>
                  <span>
                    This form reports amounts paid for qualified tuition and related expenses. Consult your tax advisor
                    or visit IRS.gov to determine if you qualify for education tax credits.
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-yellow-600 mt-1">•</span>
                  <span>
                    Box 1 or Box 2 may be used to calculate your education credit. Do not add these boxes together.
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-yellow-600 mt-1">•</span>
                  <span>
                    Scholarships and grants (Box 5) may reduce the amount of your qualified expenses for education credits.
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-yellow-600 mt-1">•</span>
                  <span>
                    Keep this form for your tax records. You may need it to complete IRS Form 8863 to claim education credits.
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-yellow-600 mt-1">•</span>
                  <span>
                    For questions about your 1098-T, contact Student Accounts at (209) 228-4444 or studentaccounts@ucmerced.edu
                  </span>
                </li>
              </ul>
            </div>
          </>
        )}
      </main>
    </div>
  );
};

export default My1098TPage;
